export interface Payee {
  name: string
  role: string // 'Loan Officer' | 'Processor' | 'Branch Manager' | 'Referral Partner'
  commissionAmount: number
  commissionNote: string // e.g., 'Flat: $4,500' or '0.50% of loan'
}

export interface FundedLoan {
  id: string
  borrowerName: string
  propertyAddress: string
  loanAmount: number
  loanType: 'Purchase' | 'Refinance' | 'Cash-Out Refi'
  fundedDate: string // ISO date string
  loanOfficer: string
  paymentModel: 'Flat Fee' | 'Regular'
  payees: Payee[]
  totalCommissions: number
}

export const mockLoans: FundedLoan[] = [
  {
    id: '1',
    borrowerName: 'James & Sarah Miller',
    propertyAddress: '123 Maple Avenue, Springfield, IL',
    loanAmount: 450000,
    loanType: 'Purchase',
    fundedDate: '2023-10-02',
    loanOfficer: 'David Chen',
    paymentModel: 'Flat Fee',
    totalCommissions: 5200,
    payees: [
      {
        name: 'David Chen',
        role: 'Loan Officer',
        commissionAmount: 4500,
        commissionNote: 'Flat Fee',
      },
      {
        name: 'Alice Johnson',
        role: 'Processor',
        commissionAmount: 500,
        commissionNote: 'Per File',
      },
      {
        name: 'Branch Override',
        role: 'Branch Manager',
        commissionAmount: 200,
        commissionNote: 'Override',
      },
    ],
  },
  {
    id: '2',
    borrowerName: 'Robert Smith',
    propertyAddress: '4502 Oak Ridge Dr, Austin, TX',
    loanAmount: 620000,
    loanType: 'Refinance',
    fundedDate: '2023-10-05',
    loanOfficer: 'Sarah Williams',
    paymentModel: 'Regular',
    totalCommissions: 9300,
    payees: [
      {
        name: 'Sarah Williams',
        role: 'Loan Officer',
        commissionAmount: 6200,
        commissionNote: '1.00% of loan',
      },
      {
        name: 'Mike Brown',
        role: 'Referral Partner',
        commissionAmount: 3100,
        commissionNote: '0.50% Split',
      },
    ],
  },
  {
    id: '3',
    borrowerName: 'Elena Rodriguez',
    propertyAddress: '789 Palm Way, Miami, FL',
    loanAmount: 325000,
    loanType: 'Purchase',
    fundedDate: '2023-10-08',
    loanOfficer: 'David Chen',
    paymentModel: 'Flat Fee',
    totalCommissions: 5000,
    payees: [
      {
        name: 'David Chen',
        role: 'Loan Officer',
        commissionAmount: 4500,
        commissionNote: 'Flat Fee',
      },
      {
        name: 'Alice Johnson',
        role: 'Processor',
        commissionAmount: 500,
        commissionNote: 'Per File',
      },
    ],
  },
  {
    id: '4',
    borrowerName: 'Michael Chang',
    propertyAddress: '555 Pine St, Seattle, WA',
    loanAmount: 850000,
    loanType: 'Purchase',
    fundedDate: '2023-10-12',
    loanOfficer: 'Jessica Lee',
    paymentModel: 'Regular',
    totalCommissions: 10625,
    payees: [
      {
        name: 'Jessica Lee',
        role: 'Loan Officer',
        commissionAmount: 8500,
        commissionNote: '1.00% of loan',
      },
      {
        name: 'Tom Wilson',
        role: 'Branch Manager',
        commissionAmount: 2125,
        commissionNote: '0.25% Override',
      },
    ],
  },
  {
    id: '5',
    borrowerName: 'William & Mary Davis',
    propertyAddress: '882 Sunset Blvd, Los Angeles, CA',
    loanAmount: 750000,
    loanType: 'Cash-Out Refi',
    fundedDate: '2023-10-15',
    loanOfficer: 'David Chen',
    paymentModel: 'Flat Fee',
    totalCommissions: 5500,
    payees: [
      {
        name: 'David Chen',
        role: 'Loan Officer',
        commissionAmount: 4500,
        commissionNote: 'Flat Fee',
      },
      {
        name: 'Alice Johnson',
        role: 'Processor',
        commissionAmount: 500,
        commissionNote: 'Per File',
      },
      {
        name: 'Marketing Fund',
        role: 'Branch Manager',
        commissionAmount: 500,
        commissionNote: 'Marketing Fee',
      },
    ],
  },
  {
    id: '6',
    borrowerName: 'Jennifer Lopez',
    propertyAddress: '221 B Baker St, London, OH',
    loanAmount: 280000,
    loanType: 'Purchase',
    fundedDate: '2023-10-18',
    loanOfficer: 'Sarah Williams',
    paymentModel: 'Regular',
    totalCommissions: 3500,
    payees: [
      {
        name: 'Sarah Williams',
        role: 'Loan Officer',
        commissionAmount: 2800,
        commissionNote: '1.00% of loan',
      },
      {
        name: 'Processor Fee',
        role: 'Processor',
        commissionAmount: 700,
        commissionNote: 'Contract Processing',
      },
    ],
  },
  {
    id: '7',
    borrowerName: 'Thomas Anderson',
    propertyAddress: '101 Matrix Rd, Chicago, IL',
    loanAmount: 420000,
    loanType: 'Refinance',
    fundedDate: '2023-10-20',
    loanOfficer: 'Marcus Johnson',
    paymentModel: 'Flat Fee',
    totalCommissions: 4000,
    payees: [
      {
        name: 'Marcus Johnson',
        role: 'Loan Officer',
        commissionAmount: 3500,
        commissionNote: 'Flat Fee (Tier 1)',
      },
      {
        name: 'Admin Fee',
        role: 'Branch Manager',
        commissionAmount: 500,
        commissionNote: 'Admin',
      },
    ],
  },
  {
    id: '8',
    borrowerName: 'Emily Blunt',
    propertyAddress: '404 Quiet Place, New York, NY',
    loanAmount: 950000,
    loanType: 'Purchase',
    fundedDate: '2023-10-22',
    loanOfficer: 'Jessica Lee',
    paymentModel: 'Regular',
    totalCommissions: 11875,
    payees: [
      {
        name: 'Jessica Lee',
        role: 'Loan Officer',
        commissionAmount: 9500,
        commissionNote: '1.00% of loan',
      },
      {
        name: 'Tom Wilson',
        role: 'Branch Manager',
        commissionAmount: 2375,
        commissionNote: '0.25% Override',
      },
    ],
  },
  {
    id: '9',
    borrowerName: 'Chris Evans',
    propertyAddress: '1940 Captain Dr, Brooklyn, NY',
    loanAmount: 550000,
    loanType: 'Cash-Out Refi',
    fundedDate: '2023-10-25',
    loanOfficer: 'Sarah Williams',
    paymentModel: 'Regular',
    totalCommissions: 6875,
    payees: [
      {
        name: 'Sarah Williams',
        role: 'Loan Officer',
        commissionAmount: 5500,
        commissionNote: '1.00% of loan',
      },
      {
        name: 'Referral',
        role: 'Referral Partner',
        commissionAmount: 1375,
        commissionNote: '0.25% Split',
      },
    ],
  },
  {
    id: '10',
    borrowerName: 'Tony Stark',
    propertyAddress: '1 Malibu Point, Malibu, CA',
    loanAmount: 1200000,
    loanType: 'Refinance',
    fundedDate: '2023-10-28',
    loanOfficer: 'Marcus Johnson',
    paymentModel: 'Flat Fee',
    totalCommissions: 6000,
    payees: [
      {
        name: 'Marcus Johnson',
        role: 'Loan Officer',
        commissionAmount: 5500,
        commissionNote: 'Flat Fee (Jumbo)',
      },
      {
        name: 'Processing',
        role: 'Processor',
        commissionAmount: 500,
        commissionNote: 'Per File',
      },
    ],
  },
]
